import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import MenuItem from "./MenuItem";
import { detectCategories, getCategoryName } from "./utils/categoryDetector";
import {
  getProgressStats,
  getRecentlyViewed,
  markAsViewed,
} from "./utils/progressTracker";
import { getIconForLabel } from "./utils/iconMapper";
import { useTranslation } from "../../hooks/useTranslation";
import { translateTreeLabel } from "../../utils/translateTreeLabel";
import "./KnowledgeBaseMenu.css";

/**
 * KnowledgeBaseMenu Component - Redesigned
 * Modern card-based UI with featured guides, topic grid, and articles list
 */
function KnowledgeBaseMenu({
  onNodeClick,
  rootId = "start",
  selectedNodeId,
  tree,
  treeType = "business",
}) {
  const t = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // 'all' or category id
  const [expandedCategories, setExpandedCategories] = useState({});
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [bookmarks, setBookmarks] = useState({});
  const [progressStats, setProgressStats] = useState({
    percentage: 0,
    total: 0,
    viewed: 0,
  });
  const activeItemRef = useRef(null);
  const menuContainerRef = useRef(null);

  // Detect categories from tree structure
  const categories = detectCategories(tree, rootId);

  // Get all viewable articles (nodes with pages)
  const allArticles = useMemo(() => {
    const articles = [];
    const processNode = (nodeId, categoryId = null) => {
      const node = tree[nodeId];
      if (!node) return;

      if (node.page) {
        articles.push({
          id: nodeId,
          label: node.label,
          categoryId: categoryId || nodeId,
          node,
        });
      }

      if (node.children) {
        node.children.forEach((childId) =>
          processNode(childId, categoryId || nodeId)
        );
      }
    };

    categories.forEach((cat) => {
      processNode(cat.id, cat.id);
    });

    return articles;
  }, [tree, categories]);

  // Featured articles (first 3-4 from different categories)
  const featuredArticles = useMemo(() => {
    const featured = [];
    const usedCategories = new Set();

    for (const article of allArticles) {
      if (featured.length >= 3) break;
      if (!usedCategories.has(article.categoryId)) {
        featured.push(article);
        usedCategories.add(article.categoryId);
      }
    }

    return featured;
  }, [allArticles]);

  // Load recently viewed and progress on mount
  useEffect(() => {
    setRecentlyViewed(getRecentlyViewed());
    setProgressStats(getProgressStats(tree, rootId));

    // Load bookmarks from localStorage
    const savedBookmarks = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith("bookmark_") && localStorage.getItem(key) === "true") {
        savedBookmarks[key.replace("bookmark_", "")] = true;
      }
    }
    setBookmarks(savedBookmarks);

    // Expand all categories by default
    const defaultExpanded = {};
    categories.forEach((cat) => {
      defaultExpanded[cat.id] = true;
    });
    setExpandedCategories(defaultExpanded);
  }, [tree, rootId]);

  // Update progress when selected node changes
  useEffect(() => {
    if (selectedNodeId) {
      const node = tree[selectedNodeId];
      if (node && node.label) {
        markAsViewed(selectedNodeId, node.label);
        setRecentlyViewed(getRecentlyViewed());
        setProgressStats(getProgressStats(tree, rootId));
      }
    }
  }, [selectedNodeId, tree, rootId]);

  const handleNodeClick = useCallback(
    (node) => {
      if (onNodeClick) {
        onNodeClick(node);
      }
    },
    [onNodeClick]
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleFilterClick = (filterId) => {
    setActiveFilter(filterId);
  };

  const handleArticleClick = (article) => {
    const node = tree[article.id];
    if (node && onNodeClick) {
      onNodeClick({ id: article.id, ...node });
    }
  };

  const handleCategoryCardClick = (categoryId) => {
    setActiveFilter(categoryId);
  };

  // Filter articles based on search and active filter
  const filteredArticles = useMemo(() => {
    let filtered = allArticles;

    if (activeFilter !== "all") {
      filtered = filtered.filter((a) => a.categoryId === activeFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((a) => a.label.toLowerCase().includes(query));
    }

    return filtered;
  }, [allArticles, activeFilter, searchQuery]);

  // Get category info
  const getCategoryInfo = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return { icon: "📄", name: "" };
    const translatedLabel = translateTreeLabel(
      category.id,
      category.label,
      t,
      treeType
    );
    return {
      icon: category.icon || "📚",
      name: getCategoryName(translatedLabel),
    };
  };

  // Count articles per category
  const categoryArticleCounts = useMemo(() => {
    const counts = {};
    allArticles.forEach((a) => {
      counts[a.categoryId] = (counts[a.categoryId] || 0) + 1;
    });
    return counts;
  }, [allArticles]);

  // Get read time estimate (based on label length/complexity)
  const getReadTime = (label) => {
    const words = label.split(" ").length;
    return Math.max(3, Math.min(12, words + 2));
  };

  // Get badge for featured articles
  const getBadge = (index) => {
    const badges = [
      {
        className: "kb-badge-essential",
        text: t("knowledgeBase.badges.essential", "ESSENTIAL"),
      },
      {
        className: "kb-badge-popular",
        text: t("knowledgeBase.badges.popular", "POPULAR"),
      },
      {
        className: "kb-badge-new",
        text: t("knowledgeBase.badges.new", "ADVANCED"),
      },
    ];
    return badges[index % badges.length];
  };

  return (
    <div className="kb-menu-container kb-redesigned" ref={menuContainerRef}>
      {/* Search Bar */}
      <div className="kb-search-section">
        <div className="kb-search-wrapper">
          <span className="kb-search-icon">🔍</span>
          <input
            type="text"
            className="kb-search-input"
            placeholder={t(
              "knowledgeBase.ui.searchPlaceholder",
              "Search for LLC, Credit, Funding..."
            )}
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <button
              className="kb-search-clear"
              onClick={handleClearSearch}
              aria-label={t("knowledgeBase.ui.clearSearch", "Clear search")}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Category Filter Chips */}
      <div className="kb-filter-chips">
        <button
          className={`kb-chip ${
            activeFilter === "all" ? "kb-chip-active" : ""
          }`}
          onClick={() => handleFilterClick("all")}
        >
          {t("knowledgeBase.filters.allTopics", "All Topics")}
        </button>
        {categories.map((category) => {
          const info = getCategoryInfo(category.id);
          return (
            <button
              key={category.id}
              className={`kb-chip ${
                activeFilter === category.id ? "kb-chip-active" : ""
              }`}
              onClick={() => handleFilterClick(category.id)}
            >
              {info.name}
            </button>
          );
        })}
      </div>

      {/* Progress Bar */}
      {progressStats.total > 0 && (
        <div className="kb-progress-section">
          <div className="kb-progress-header">
            <span className="kb-progress-label">
              {t("knowledgeBase.ui.progress", "Your Progress")}
            </span>
            <span className="kb-progress-stats">
              {progressStats.viewed}/{progressStats.total} (
              {progressStats.percentage}%)
            </span>
          </div>
          <div className="kb-progress-bar">
            <div
              className="kb-progress-fill"
              style={{ width: `${progressStats.percentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Featured Guides Section - Only show when not filtering */}
      {activeFilter === "all" &&
        !searchQuery &&
        featuredArticles.length > 0 && (
          <div className="kb-section">
            <div className="kb-section-header">
              <h3 className="kb-section-title">
                {t("knowledgeBase.sections.featuredGuides", "Featured Guides")}
              </h3>
            </div>

            <div className="kb-featured-grid">
              {featuredArticles.map((article, index) => {
                const catInfo = getCategoryInfo(article.categoryId);
                const badge = getBadge(index);
                const translatedLabel = translateTreeLabel(
                  article.id,
                  article.label,
                  t,
                  treeType
                );

                return (
                  <div
                    key={article.id}
                    className={`kb-featured-card kb-featured-card-${index}`}
                    onClick={() => handleArticleClick(article)}
                  >
                    <div className="kb-featured-overlay">
                      <span className={`kb-badge ${badge.className}`}>
                        {badge.text}
                      </span>
                      <h4 className="kb-featured-title">{translatedLabel}</h4>
                      <span className="kb-featured-meta">
                        ⏱️ {getReadTime(article.label)}{" "}
                        {t("knowledgeBase.ui.minRead", "min read")}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* Browse by Topic Section - Only show when not filtering */}
      {activeFilter === "all" && !searchQuery && (
        <div className="kb-section">
          <h3 className="kb-section-title">
            {t("knowledgeBase.sections.browseByTopic", "Browse by Topic")}
          </h3>

          <div className="kb-topic-grid">
            {categories.map((category) => {
              const info = getCategoryInfo(category.id);
              const count = categoryArticleCounts[category.id] || 0;

              return (
                <div
                  key={category.id}
                  className="kb-topic-card"
                  onClick={() => handleCategoryCardClick(category.id)}
                >
                  <div className="kb-topic-icon">{info.icon}</div>
                  <div className="kb-topic-info">
                    <h4 className="kb-topic-name">{info.name}</h4>
                    <span className="kb-topic-count">
                      {count} {t("knowledgeBase.ui.articles", "Articles")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Articles List Section */}
      <div className="kb-section">
        <h3 className="kb-section-title">
          {activeFilter === "all" && !searchQuery
            ? t("knowledgeBase.sections.latestUpdates", "Latest Updates")
            : searchQuery
            ? t("knowledgeBase.sections.searchResults", "Search Results")
            : getCategoryInfo(activeFilter).name}
          {filteredArticles.length > 0 && (
            <span className="kb-count-badge">{filteredArticles.length}</span>
          )}
        </h3>

        <div className="kb-articles-list">
          {filteredArticles.length === 0 ? (
            <div className="kb-empty-state">
              <span className="kb-empty-icon">🔍</span>
              <p>
                {t(
                  "knowledgeBase.ui.noResults",
                  "No articles found. Try a different search term."
                )}
              </p>
            </div>
          ) : (
            filteredArticles.map((article) => {
              const catInfo = getCategoryInfo(article.categoryId);
              const translatedLabel = translateTreeLabel(
                article.id,
                article.label,
                t,
                treeType
              );
              const isActive = selectedNodeId === article.id;
              const icon = getIconForLabel(translatedLabel);
              const isPremium =
                article.node.premium === "true" ||
                article.node.premium === true;
              const isBookmarked = bookmarks[article.id] || false;

              const handleBookmarkClick = (e) => {
                e.stopPropagation();
                const newValue = !isBookmarked;
                localStorage.setItem(
                  `bookmark_${article.id}`,
                  newValue.toString()
                );
                setBookmarks((prev) => ({
                  ...prev,
                  [article.id]: newValue,
                }));
              };

              return (
                <div
                  key={article.id}
                  className={`kb-article-item ${
                    isActive ? "kb-article-active" : ""
                  } ${isPremium ? "kb-article-premium" : ""}`}
                  onClick={() => handleArticleClick(article)}
                  ref={isActive ? activeItemRef : null}
                >
                  <div className="kb-article-icon">{icon}</div>
                  <div className="kb-article-content">
                    <h4 className="kb-article-title">
                      {translatedLabel}
                      {isPremium && (
                        <span
                          className="kb-premium-badge"
                          title={t(
                            "knowledgeBase.ui.premiumContent",
                            "Premium Content"
                          )}
                        >
                          🔒
                        </span>
                      )}
                    </h4>
                    <div className="kb-article-meta">
                      <span className="kb-article-time">
                        ⏱️ {getReadTime(article.label)}{" "}
                        {t("knowledgeBase.ui.min", "min")}
                      </span>
                      <span className="kb-article-category">
                        {catInfo.name}
                      </span>
                      {isPremium && (
                        <span className="kb-article-premium-tag">
                          {t("knowledgeBase.ui.premium", "Premium")}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className={`kb-article-bookmark ${
                      isBookmarked ? "kb-bookmarked" : ""
                    }`}
                    title={t("knowledgeBase.ui.bookmark", "Bookmark")}
                    onClick={handleBookmarkClick}
                  >
                    {isBookmarked ? "⭐" : "☆"}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default KnowledgeBaseMenu;
